import React, { useState, useEffect, useMemo, useCallback, useRef, useImperativeHandle, type Ref } from 'react';
import { Box, Container, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Shop, Module, DeviceModule } from '@core/types';
import { moduleService } from '@/services';
import { DeleteConfirmationDialog } from '@/components';
import NewWcnTable, { DataTableColumn, KeyValuePair } from '@/components/datatable/NewWcnTable.tsx';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';
import { ShopInformationRef } from '@features/administration/components/shopSetup/ShopInformation.tsx';
import AddSoftwareModulePanel, {
    AddSoftwareModulePanelRef
} from '@features/administration/components/shopSetup/AddSoftwareModulePanel.tsx';
import { useUnsavedChangesGuard } from '@core/hooks/useUnsavedChangesGuard.ts';
import { useRegisterDirtyGuard } from '@core/hooks/useRegisterDirtyGuard.ts';

interface ModuleRow {
    id: string;
    name: string;
    kind: string;
    gatewayName: string;
    components: any[];
    module: DeviceModule;
}

const getModuleComponents = (module: Module): any[] => {
    if (!module) return [];

    switch (module.discriminator) {
        case 'TrolleyShelter':
            return module.impinjRfidReader ? [module.impinjRfidReader] : [];
        case 'ZoneTracker':
            return (
                module.zoneGates
                    ?.flatMap((g: any) => [g.sensorCamera, g.backupSensorCamera])
                    .filter((x: any) => x != null) || []
            );
        case 'LineTracker':
            return (
                module.lineGates
                    ?.flatMap((g: any) => [g.sensorCamera, g.backupSensorCamera])
                    .filter((x: any) => x != null) || []
            );
        case 'ShopAutomation':
            return module.shopAutomationStatus ? [module.shopAutomationStatus] : [];
        case 'SmartShelf':
            return module.smartShelfComponent ? [module.smartShelfComponent] : [];
        default:
            return [];
    }
};

const SoftwareModules: React.FC<{
    shop: Shop | null;
    loadingData: boolean;
    creating?: boolean;
    onCloseCreate?: (shopId?: string) => void;
    onEditing?: () => void;
    ref?: Ref<ShopInformationRef>;
}> = ({ shop, loadingData = false, creating = false, onCloseCreate, onEditing, ref }) => {
    const { t } = useTranslation();
    const { notifyErrorResponse, notifySuccess } = useNotifications();

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<ModuleRow[]>([]);
    const [showAddPanel, setShowAddPanel] = useState(false);
    const [selectedModule, setSelectedModule] = useState<DeviceModule | null>(null);
    const addSectionRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<AddSoftwareModulePanelRef>(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<ModuleRow | null>(null);

    const discardedRef = useRef(false);
    const prevCreatingRef = useRef(false);

    useImperativeHandle(
        ref,
        () => ({
            get isDirty() {
                return !discardedRef.current && !!panelRef.current?.isDirty;
            },
            save: async () => !!(await panelRef.current?.save())
        }),
        [panelRef.current?.isDirty]
    );

    useEffect(() => {
        if (creating && !prevCreatingRef.current && shop) {
            discardedRef.current = false;
            setSelectedModule(null);
            setShowAddPanel(true);
        }
        prevCreatingRef.current = creating;
    }, [creating, shop]);

    useEffect(() => {
        if (showAddPanel || selectedModule) {
            addSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [showAddPanel, selectedModule]);

    useEffect(() => {
        if (!shop) {
            setRows([]);
            return;
        }

        const result: ModuleRow[] = [];

        (shop.gateways || []).forEach((gateway) => {
            (gateway.modules || [])
                .filter((module) => !module.deleteDate)
                .forEach((module) => {
                    result.push({
                        id: module.id,
                        name: module.moduleId || '-',
                        kind: module.discriminator || '',
                        gatewayName: gateway.deviceId,
                        components: getModuleComponents(module),
                        module: { ...module, kind: module.discriminator || '', gateway } as DeviceModule
                    });
                });
        });

        setRows(result);
    }, [shop]);

    const handleDeleteClick = useCallback((row: ModuleRow) => {
        setRowToDelete(row);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!rowToDelete) return;

        setLoading(true);
        setDeleteDialogOpen(false);

        try {
            await moduleService.sendModuleToRecycle(rowToDelete.kind, rowToDelete.id);
            notifySuccess();
            setRows((prev) => prev.filter((r) => r.id !== rowToDelete.id));
            onCloseCreate?.(shop?.id);
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
            setRowToDelete(null);
        }
    }, [rowToDelete, notifyErrorResponse, notifySuccess, onCloseCreate, shop]);

    const handleCloseEditor = useCallback(
        (shouldReload = false) => {
            discardedRef.current = true;
            setShowAddPanel(false);
            setSelectedModule(null);
            onCloseCreate?.(shouldReload ? shop?.id : undefined);
        },
        [onCloseCreate, shop]
    );

    const save = useCallback(async () => {
        const saved = await panelRef.current?.save();
        if (saved) {
            handleCloseEditor(true);
        }
        return !!saved;
    }, [handleCloseEditor]);

    const discard = useCallback(() => {
        handleCloseEditor();
    }, [handleCloseEditor]);

    const isDirty = useCallback(() => !discardedRef.current && !!panelRef.current?.isDirty, []);
    const { guard } = useUnsavedChangesGuard({ isDirty, save });
    useRegisterDirtyGuard({ checkDirty: isDirty, save, discard });

    const handleEdit = useCallback(
        (row: ModuleRow) => {
            guard(() => {
                discardedRef.current = false;
                onEditing?.();
                setShowAddPanel(false);
                setSelectedModule(row.module);
            });
        },
        [guard, onEditing]
    );

    const handleCancelEditor = useCallback(() => {
        guard(() => handleCloseEditor());
    }, [guard, handleCloseEditor]);

    const columns: DataTableColumn<ModuleRow>[] = useMemo(
        () => [
            {
                text: t('name'),
                value: 'name',
                width: 260,
                sortable: true
            },
            {
                text: t('components'),
                value: 'components',
                width: 320,
                sortable: true,
                valueGetter: (row) => row.components.map((c) => c?.name || c?.deviceId || '').join(', '),
                multiLine: true,
                multiLineValue: (row): KeyValuePair[] =>
                    row.components.length
                        ? row.components.map((c, i) => ({
                              key: c?.id ?? String(i),
                              value: c?.name || c?.deviceId || '-'
                          }))
                        : [{ key: 'empty', value: '-' }]
            },
            {
                text: t('gateway'),
                value: 'gatewayName',
                width: 200,
                sortable: false
            },
            {
                text: t('ipAddress'),
                value: 'ipAddress',
                width: 200,
                sortable: false,
                multiLine: true,
                multiLineValue: (row): KeyValuePair[] =>
                    row.components.length
                        ? row.components.map((c, i) => ({ key: c?.id ?? String(i), value: c?.ipAddress || '-' }))
                        : [{ key: 'empty', value: '-' }]
            },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                align: 'left',
                sortable: false,
                isAction: true,
                render: (row) => (
                    <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap' }}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                            }}
                            title={t('edit')}
                        >
                            <i className="bi bi-pencil" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(row);
                            }}
                            title={t('delete')}
                        >
                            <i className="bi bi-trash3" />
                        </IconButton>
                    </Box>
                )
            }
        ],
        [t, handleDeleteClick, handleEdit]
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', overflow: 'visible' }}>
            <Container maxWidth={false} sx={{ flex: 1, py: 3 }}>
                {!loading && (
                    <NewWcnTable
                        columns={columns}
                        data={rows}
                        loading={loading}
                        noDataMessage={t('noModules')}
                        size="small"
                    />
                )}

                {(showAddPanel || selectedModule) && shop && (
                    <Box ref={addSectionRef} sx={{ mt: 3 }}>
                        <AddSoftwareModulePanel
                            key={selectedModule?.id ?? 'new'}
                            ref={panelRef}
                            shopId={shop.id}
                            initialModule={selectedModule}
                            onCancel={handleCancelEditor}
                            onChanged={() => handleCloseEditor(true)}
                        />
                    </Box>
                )}
            </Container>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                heading={t('deleteHeading')}
                message={t('deleteModuleMessage', { item: rowToDelete?.name || '' })}
                onConfirm={handleDeleteConfirm}
                onCancel={() => {
                    setDeleteDialogOpen(false);
                    setRowToDelete(null);
                }}
            />

            <LoadingOverlay open={loading || loadingData} />
        </Box>
    );
};

SoftwareModules.displayName = 'SoftwareModules Administration';
export default SoftwareModules;
